import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Badge, Table, Alert, Spinner, Card } from 'react-bootstrap';
import api from '../../services/api';
import './LinkGenerator.css';

const LinkGenerator = ({ show, onHide, cliente = null }) => {
    const [nome, setNome] = useState('');
    const [email, setEmail] = useState('');
    const [destino, setDestino] = useState('home');
    const [loading, setLoading] = useState(false);
    const [linkGerado, setLinkGerado] = useState(null);
    const [copiado, setCopiado] = useState(false);
    const [erro, setErro] = useState(null);

    // Preencher com dados do cliente se fornecido
    useEffect(() => {
        if (cliente) {
            setNome(cliente.contato_nome || cliente.nome || '');
            setEmail(cliente.email || '');
        }
    }, [cliente]);

    // Limpar ao abrir
    useEffect(() => {
        if (show) {
            setLinkGerado(null);
            setCopiado(false);
            setErro(null);
        }
    }, [show]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErro(null);

        try {
            const response = await api.post('/api/ecommerce-bling/generate-link', {
                nome,
                email,
                clienteId: cliente?.contato_id || cliente?.codigo_cliente || null,
                destino
            });

            if (response.data.success) {
                setLinkGerado(response.data);
            } else {
                setErro(response.data.message || 'Erro ao gerar link');
            }
        } catch (err) {
            setErro(err.message || 'Erro ao gerar link');
        } finally {
            setLoading(false);
        }
    };

    const copiarLink = () => {
        if (linkGerado?.link) {
            navigator.clipboard.writeText(linkGerado.link);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
        }
    };

    const copiarMensagem = () => {
        const mensagem = `Olá ${nome.split(' ')[0]}! 👋

Temos novidades especiais esperando por você! 🎁

Acesse agora e confira: ${linkGerado.link}

Qualquer dúvida, estamos à disposição!
Equipe Loja Gringão 💙`;

        navigator.clipboard.writeText(mensagem);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-semibold">Gerar Link Rastreável</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!linkGerado ? (
                    <Form onSubmit={handleSubmit}>
                        {erro && (
                            <Alert variant="danger" className="mb-3">
                                {erro}
                            </Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Nome do Cliente *</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Digite o nome do cliente"
                                value={nome}
                                onChange={(e) => setNome(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Email (opcional)</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="email@exemplo.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Destino do Link</Form.Label>
                            <Form.Select
                                value={destino}
                                onChange={(e) => setDestino(e.target.value)}
                            >
                                <option value="home">Página Inicial</option>
                                <option value="promocoes">Promoções</option>
                                <option value="novidades">Novidades / Lançamentos</option>
                            </Form.Select>
                        </Form.Group>

                        <div className="d-grid">
                            <Button type="submit" variant="primary" disabled={loading || !nome}>
                                {loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Gerando...
                                    </>
                                ) : (
                                    'Gerar Link Rastreável'
                                )}
                            </Button>
                        </div>
                    </Form>
                ) : (
                    <div>
                        <Alert variant="success" className="mb-3">
                            Link gerado com sucesso!
                        </Alert>

                        <Card className="mb-3">
                            <Card.Body>
                                <div className="mb-2">
                                    <small className="text-muted">Link para {nome}:</small>
                                </div>
                                <div className="d-flex gap-2 align-items-center">
                                    <code className="flex-grow-1 p-2 bg-light rounded text-break" style={{ fontSize: '0.85rem' }}>
                                        {linkGerado.link}
                                    </code>
                                </div>
                            </Card.Body>
                        </Card>

                        <div className="d-flex gap-2 mb-3">
                            <Button variant="outline-primary" className="flex-grow-1" onClick={copiarLink}>
                                {copiado ? '✓ Copiado!' : 'Copiar Link'}
                            </Button>
                            <Button variant="primary" className="flex-grow-1" onClick={copiarMensagem}>
                                Copiar com Mensagem
                            </Button>
                        </div>

                        <div className="text-center">
                            <small className="text-muted">
                                Código de rastreamento: <strong>{linkGerado.codigo}</strong>
                            </small>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0">
                {linkGerado ? (
                    <>
                        <Button variant="outline-secondary" onClick={() => setLinkGerado(null)}>
                            Gerar Outro
                        </Button>
                        <Button variant="secondary" onClick={onHide}>
                            Fechar
                        </Button>
                    </>
                ) : (
                    <Button variant="outline-secondary" onClick={onHide}>
                        Cancelar
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

// Componente para visualizar os links gerados
export const LinksRastreaveis = () => {
    const [links, setLinks] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [syncing, setSyncing] = useState(false);

    const loadLinks = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/ecommerce-bling/links-personalizados');
            if (response.data.success) {
                setLinks(response.data.data);
                setStats(response.data.stats);
            }
        } catch (err) {
            console.error('Erro ao carregar links:', err);
        } finally {
            setLoading(false);
        }
    };

    const syncConversions = async () => {
        try {
            setSyncing(true);
            await api.post('/api/ecommerce-bling/sync-link-conversions');
            await loadLinks(); // Recarregar dados
        } catch (err) {
            console.error('Erro ao sincronizar:', err);
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        loadLinks();
    }, []);

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <div className="links-rastreaveis">
            {/* Estatísticas */}
            {stats && (
                <Row className="g-3 mb-4">
                    <Col md={2}>
                        <Card className="stat-card">
                            <Card.Body className="text-center">
                                <div className="stat-value">{stats.totalLinks}</div>
                                <div className="stat-label">Links Gerados</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="stat-card stat-clicks">
                            <Card.Body className="text-center">
                                <div className="stat-value">{stats.totalCliques}</div>
                                <div className="stat-label">Cliques</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="stat-card stat-cart">
                            <Card.Body className="text-center">
                                <div className="stat-value">{stats.totalCarrinho}</div>
                                <div className="stat-label">Carrinho</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="stat-card stat-purchase">
                            <Card.Body className="text-center">
                                <div className="stat-value">{stats.totalCompras}</div>
                                <div className="stat-label">Compras</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="stat-card stat-rate">
                            <Card.Body className="text-center">
                                <div className="stat-value">{stats.taxaClique}%</div>
                                <div className="stat-label">Taxa Clique</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="stat-card stat-revenue">
                            <Card.Body className="text-center">
                                <div className="stat-value">R$ {stats.valorTotal.toFixed(0)}</div>
                                <div className="stat-label">Valor Total</div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Header com botões */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 fw-semibold">Links Gerados</h6>
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={syncConversions}
                        disabled={syncing}
                    >
                        {syncing ? 'Sincronizando...' : '🔄 Sincronizar Conversões'}
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => setShowGenerateModal(true)}>
                        + Novo Link
                    </Button>
                </div>
            </div>

            {/* Tabela de links */}
            {links.length > 0 ? (
                <Table hover responsive size="sm">
                    <thead>
                        <tr>
                            <th>Cliente</th>
                            <th>Email</th>
                            <th>Data</th>
                            <th className="text-center">Clicou</th>
                            <th className="text-center">Carrinho</th>
                            <th className="text-center">Comprou</th>
                            <th className="text-end">Valor</th>
                        </tr>
                    </thead>
                    <tbody>
                        {links.map((link) => (
                            <tr key={link.id}>
                                <td className="fw-medium">{link.nome_cliente}</td>
                                <td className="text-muted">{link.email_cliente || '-'}</td>
                                <td>{new Date(link.data_criacao).toLocaleDateString('pt-BR')}</td>
                                <td className="text-center">
                                    <Badge bg={link.clicou ? 'success' : 'secondary'}>
                                        {link.clicou ? 'Sim' : 'Não'}
                                    </Badge>
                                </td>
                                <td className="text-center">
                                    <Badge bg={link.adicionou_carrinho ? 'warning' : 'secondary'}>
                                        {link.adicionou_carrinho ? 'Sim' : 'Não'}
                                    </Badge>
                                </td>
                                <td className="text-center">
                                    <Badge bg={link.comprou ? 'success' : 'secondary'}>
                                        {link.comprou ? 'Sim' : 'Não'}
                                    </Badge>
                                </td>
                                <td className="text-end">
                                    {link.comprou ? (
                                        <span className="text-success fw-semibold">
                                            R$ {(link.valor_compra || 0).toFixed(2)}
                                        </span>
                                    ) : '-'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            ) : (
                <div className="text-center text-muted py-4">
                    Nenhum link gerado ainda. Clique em "+ Novo Link" para começar.
                </div>
            )}

            {/* Modal de geração */}
            <LinkGenerator
                show={showGenerateModal}
                onHide={() => {
                    setShowGenerateModal(false);
                    loadLinks(); // Atualizar lista
                }}
            />
        </div>
    );
};

export default LinkGenerator;
